import BlurLinearOutlined from "@mui/icons-material/BlurLinearOutlined";
import { Button, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { includes, isEmpty, uniqWith } from "lodash-es";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { fromISO8601_DATE, toISO8601, toISO8601_DATE } from "../../../utils/iso8601";
import { CrudDataGrid } from "../../generic/components/crud-data-grid/crud-data-grid";
import { RelatedMomentsDialog } from "../../generic/components/crud-data-grid/related-moments-dialog";
import { SupabaseAccountPersonFieldService } from "../server/supabase-account-person-field-service";
import {
  SupabaseEventService,
  SupabaseScheduleEventService,
} from "../server/supabase-event-service";
import { eventKeyValidator } from "../types";
import { ConfirmDeleteDialog } from "./confirm-delete-dialog";

import type { Database } from "../../../__definitions__/database-definitions";
import type { ISO8601_DATE } from "../../../utils/iso8601";
import type { UUID } from "../../../utils/uuid";
import type yup from "../../../utils/yup";
import type { RelatedMoments } from "../../generic/components/crud-data-grid/related-moments-dialog";
import type { Columns, Row, RowOptions } from "../../generic/components/crud-data-grid/types";
import type { FC } from "react";

const SOURCE_EDITING_WHITELIST = ["MANUAL", "WEB"];

type EventRow = Database["public"]["Views"]["events_with_related_moments_calendar"]["Row"] & {
  id: UUID;
  moments: { id: UUID; title: string }[];
};

export const EventCrudDataGrid: FC<{
  events: Database["public"]["Views"]["events_with_related_moments_calendar"]["Row"][];
}> = ({ events }) => {
  const [deleteEvent, setDeleteEvent] = useState<{
    event: Omit<(typeof processedEvents)[0], "options">;
    resolve: (value: boolean | PromiseLike<boolean>) => void;
  }>();
  const dialog = useDialog<RelatedMoments>();

  const processedEvents: (EventRow & { options: RowOptions })[] = uniqWith(
    events,
    (currentVal, otherVal) => currentVal.key === otherVal.key,
  ).map((datum) => ({
    ...datum,

    id: datum.id!,
    key: datum.key,
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    moments: datum.moments as { id: UUID; title: string }[],
    options: {
      preventEditing: !includes(SOURCE_EDITING_WHITELIST, datum.source),
      preventDeletion: !includes(SOURCE_EDITING_WHITELIST, datum.source) || !isEmpty(datum.moments),
      preventEditingReason: "Unable to edit, this event is managed automatically",
    },
  }));

  const onUpdate = useSupabaseCallback(async ({ supabase }, event: EventRow) => {
    const scheduleEventService = new SupabaseScheduleEventService(supabase);
    let scheduleEventResp = await scheduleEventService.update(
      event.key!,

      { title: event.title! },
    );
    if (scheduleEventResp.status === 404) {
      scheduleEventResp = await scheduleEventService.insert({
        title: event.title!,

        event_key: event.key!,
      });
    }

    if (!scheduleEventResp.data || scheduleEventResp.error) {
      toast.error("Something went wrong");
      return;
    }
    const { data, error } = await new SupabaseEventService(supabase).update(event.id, {
      date: event.date!,
      repeat: event.repeat,
    });

    if (!data || error) {
      toast.error("Something went wrong");
      return;
    }
    toast.success("Saved");
    return {
      ...event,
      ...data,
    };
  }, []);

  const onAdd = useSupabaseCallback(async ({ supabase }, event: EventRow) => {
    const {
      data: dataSchedule,
      error: errorSchedule,
      status,
    } = await new SupabaseScheduleEventService(supabase).insert({
      event_key: event.key!,

      title: event.title!,
    });

    if (status === 409) {
      toast.error("This name has been used before. Please provide a unique tag name.");
      return;
    }
    if (!dataSchedule || errorSchedule) {
      toast.error("Something went wrong");
      return;
    }
    const { data, error } = await new SupabaseEventService(supabase).insert({
      id: event.id,

      key: event.key!,

      source: event.source!,

      date: event.date!,
      repeat: event.repeat,
      number_of_repeats: event.repeat ? 100 : 0,
    });
    if (!data || error) {
      toast.error("Something went wrong");
      return;
    }
    await new SupabaseAccountPersonFieldService(supabase).insert({
      key: `event_${event.key!}`,

      display_name: event.title!,
      merge_sample: "December 25th",
      connection_merge_sample: "December 25th",
      merge_field: true,
      connection_merge_field: false,
      channel_merge_field: true,
      segmentation_field: false,
      allow_initial: true,
      allow_repeats: true,
      field_type: "DATE",
    });
    toast.success("Saved");
    return {
      ...event,
      ...data,
    };
  }, []);

  const deleteRecord = useSupabaseCallback(async ({ supabase }, event: EventRow) => {
    const { data, error } = await new SupabaseEventService(supabase).update(event.id, {
      deleted_at: toISO8601(new Date()),
    });

    if (!data || error) {
      toast.error("Something went wrong");
      return;
    }
    toast.success("Deleted");
    return data;
  }, []);

  const columns: Columns<EventRow> = {
    title: {
      headerName: "Event Title",
      flex: 3,
      editable: true,
      valueGetter: (_, row) => row.title ?? "-",
    },
    key: {
      headerName: "Key/Merge Tag",
      flex: 1,
      editable: true,
      options: {
        validator: async (value): Promise<string | undefined> =>
          await eventKeyValidator
            .validate(value)
            .then(() => undefined)
            .catch((error: yup.ValidationError) => error.errors.join("\n")),
      },
    },
    date: {
      headerName: "Date",
      flex: 1,
      type: "date" as const,
      editable: true,
      valueGetter: (_, row) =>
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        (row.date ? fromISO8601_DATE(row.date) : null) as ISO8601_DATE | null,
      valueSetter: (value, row) => ({
        ...row,
        date: typeof value === "string" ? toISO8601_DATE(new Date(value)) : toISO8601_DATE(value),
      }),
    },
    repeat: {
      headerName: "Repeat",
      flex: 1,
      type: "string",
      editable: true,
      valueFormatter: (value) => value || "Does not repeat",
    },
    moments: {
      headerName: "Related Communications",
      flex: 1,
      editable: false,
      renderCell: (params) => {
        const numMoments = params.row.moments?.length ?? 0;
        return (
          <Button
            data-analytics-id="calendar-event-table-related-moments"
            onClick={() =>
              dialog.handleOpen({ title: params.row.title || "", moments: params.row.moments })
            }
            disabled={numMoments === 0}
          >
            {`${numMoments} Communications`}
          </Button>
        );
      },
    },
  };

  return (
    <>
      <CrudDataGrid<Row<EventRow>>
        initialRows={processedEvents}
        columns={columns}
        initialData={{
          title: "",

          date: toISO8601_DATE(new Date())!,
          key: "",
          source: "MANUAL",
          repeat: null,
        }}
        onUpdate={onUpdate}
        onAdd={onAdd}
        onDelete={(event) =>
          new Promise<boolean>((resolve) => {
            // Save the arguments to resolve or reject the promise later
            setDeleteEvent({ resolve, event });
          })
        }
        gridOverrides={{
          isCellEditable: (params) => {
            const row: Row<EventRow> = params.row;
            if (row.options?.preventEditing) return false;
            // Only allow setting `key` on new fields or those without a value,
            // no editing of existing keys.
            if (params.colDef?.field === "key") return row.isNew || isEmpty(row.key);
            return true;
          },
        }}
        modifyActions={(actions, row) => [
          ...actions,
          ...(row
            ? [
                <GridActionsCellItem
                  key={`timeline${row.id}`}
                  icon={
                    <Tooltip title="View Timeline">
                      <BlurLinearOutlined />
                    </Tooltip>
                  }
                  label="timeline"
                  onClick={() => {
                    if (row.key) window.open(`/timeline/${row.key}`, "_blank");
                  }}
                  disabled={row.moments ? row.moments.length === 0 : true}
                />,
              ]
            : []),
        ]}
      />
      <RelatedMomentsDialog subjectNamedInTitle="event" dialog={dialog} />
      {deleteEvent && (
        <ConfirmDeleteDialog
          event={deleteEvent.event}
          onClose={() => {
            deleteEvent.resolve(false);
            setDeleteEvent(undefined);
          }}
          onConfirm={async () => {
            await deleteRecord(deleteEvent.event);
            deleteEvent.resolve(true);
            setDeleteEvent(undefined);
          }}
        />
      )}
    </>
  );
};
